import { PaginatedQueryResponse } from '@/types/api'
import { CompanySize, IProfile, SeniorityLevel } from '@/types/profile'
import { IToolSuggestion } from '@/types/tool-suggestion'
import { ApiService } from './ApiService'

export type IUserProfilePostPayload = {
  companyName?: string
  companySize?: CompanySize
  companyDescription?: string
  workArea?: string
  seniorityLevel?: SeniorityLevel
  companyIndustry?: string
  workAreaCoreTasks?: string[]
  workAreaCoreTasksNotes?: string
  tools?: string[]
  exploreNewTools?: boolean
}

export type IUserProfilePatchPayload = IUserProfilePostPayload

/** Only forward onboarding-relevant fields that are actually set. */
function buildProfilePayload(
  payload: IUserProfilePostPayload | IUserProfilePatchPayload
) {
  return {
    ...(payload.companyName && { companyName: payload.companyName }),
    ...(payload.companySize && { companySize: payload.companySize }),
    ...(payload.companyDescription !== undefined && {
      companyDescription: payload.companyDescription,
    }),
    ...(payload.workArea && { workArea: payload.workArea }),
    ...(payload.seniorityLevel && { seniorityLevel: payload.seniorityLevel }),
    ...(payload.companyIndustry && {
      companyIndustry: payload.companyIndustry,
    }),
    ...(Array.isArray(payload.workAreaCoreTasks) && {
      workAreaCoreTasks: payload.workAreaCoreTasks,
    }),
    ...(payload.workAreaCoreTasksNotes !== undefined && {
      workAreaCoreTasksNotes: payload.workAreaCoreTasksNotes,
    }),
    ...(Array.isArray(payload.tools) && { tools: payload.tools }),
    ...(payload.exploreNewTools !== undefined && {
      exploreNewTools: payload.exploreNewTools,
    }),
  }
}

export class UserProfileService extends ApiService {
  static GET_PATH = '/profiles'
  static POST_PATH = '/profiles'
  static PATCH_PATH = '/profiles/:id'

  static getPatchPath(profileId: string) {
    return UserProfileService.PATCH_PATH.replace(':id', profileId)
  }

  static getPostPath() {
    return UserProfileService.POST_PATH
  }

  static getWorkAreaTaskSuggestionsPath(profileId: string) {
    return `${UserProfileService.GET_PATH}/${profileId}/work-area-task-suggestions`
  }

  static getToolsSuggestionsPath(profileId: string) {
    return `${UserProfileService.GET_PATH}/${profileId}/tools-suggestions`
  }

  static async getToolsSuggestions(
    profileId: string
  ): Promise<PaginatedQueryResponse<IToolSuggestion>> {
    const raw = (await ApiService.post(
      UserProfileService.getToolsSuggestionsPath(profileId),
      {}
    )) as
      | {
          data?: unknown
          metadata?: { page: number; limit: number; total: number }
        }
      | unknown[]
    const data = Array.isArray((raw as { data?: unknown })?.data)
      ? (raw as { data: unknown[] }).data
      : Array.isArray(raw)
        ? raw
        : []
    const metadata = (
      raw as { metadata?: { page: number; limit: number; total: number } }
    )?.metadata ?? {
      page: 1,
      limit: data.length,
      total: data.length,
    }
    return {
      data: data as IToolSuggestion[],
      metadata,
    }
  }

  static async postWorkAreaTaskSuggestions(
    profileId: string,
    body: Record<string, unknown> = {}
  ) {
    return ApiService.post(
      UserProfileService.getWorkAreaTaskSuggestionsPath(profileId),
      body
    )
  }

  static async getProfile() {
    const { data: profiles, ...rest } = await ApiService.get(
      UserProfileService.GET_PATH
    )
    return { data: profiles?.[profiles.length - 1] as IProfile, ...rest }
  }

  static async postUserProfile(payload: IUserProfilePostPayload) {
    return ApiService.post(
      UserProfileService.getPostPath(),
      buildProfilePayload(payload)
    )
  }

  static async patchUserProfile(
    profileId: string,
    payload: IUserProfilePatchPayload
  ) {
    return ApiService.patch(
      UserProfileService.getPatchPath(profileId),
      buildProfilePayload(payload)
    )
  }
}
